import type { IPartner } from '@/types';

export interface IOSCRuntimeConfig {
  jwtToken?: string | null;
  ezconnexProxyUrl?: string | null;
  currentPartner?: IPartner | null;
}

declare global {
  interface Window {
    __OSC_RUNTIME__?: IOSCRuntimeConfig;
  }
}

function readRuntimeConfig(): IOSCRuntimeConfig | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.__OSC_RUNTIME__ ?? null;
}

export function hasRuntimeConfig() {
  return readRuntimeConfig() !== null;
}

export function getRuntimeConfig() {
  return readRuntimeConfig();
}

export function getEzconnexProxyUrl(): string {
  return readRuntimeConfig()?.ezconnexProxyUrl
    ?? '/backend/proxy.php?upstream=ezconnex-api';
}

export function getJwtToken(): string | null {
  return readRuntimeConfig()?.jwtToken ?? null;
}

function jwtExp(token: string): number | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

let _refreshPromise: Promise<string | null> | null = null;

function refreshJwtToken(): Promise<string | null> {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = fetch('/backend/token.php')
    .then(r => r.json())
    .then((data: { token?: string }) => {
      const token = data.token ?? null;
      if (token && window.__OSC_RUNTIME__) {
        window.__OSC_RUNTIME__.jwtToken = token;
      }
      return token;
    })
    .catch(() => null)
    .finally(() => { _refreshPromise = null; });

  return _refreshPromise;
}

export async function getFreshJwtToken(): Promise<string | null> {
  const token = getJwtToken();
  if (!token) return null;

  const exp = jwtExp(token);
  // Refresh if expiring within 60 seconds
  if (exp !== null && exp - 60 < Math.floor(Date.now() / 1000)) {
    return refreshJwtToken();
  }

  return token;
}

export function getRuntimePartner() {
  return readRuntimeConfig()?.currentPartner ?? null;
}
